import { FC, useEffect, useState } from "react";
import { FactSection, Question, Tender, TenderAnswer, Tendering, TenderingFactMapping, TenderingPart } from "../../../models/tendering";
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Card, CardHeader, Divider, Link, Subtitle2, Tab, Table, TableCell, TableRow, Text, Title3, Checkbox, Caption1, AccordionToggleEventHandler } from "@fluentui/react-components";
import AnswerChange from "../Dialogs/AnswerChange";
import { set } from "react-hook-form";
import { useAppContext } from "../../../AppContext";
import { getDocumentsByModule } from "../../../services/documentService";

interface TenderFactsProps{
    tenderDocument: Tender;
    tendering: Tendering;
    updateTender?: (tender: Tender) => void;
    overviewQuestionID?: number;
    mode?: 'facts' | 'hidden';
}

const TenderFacts: FC<TenderFactsProps> = (props: TenderFactsProps) => {
    const [answers, setAnswers] = useState<TenderAnswer[]>(props.tenderDocument.tenderAnswers ?? []);
    const [sections, setSections] = useState<FactSection[]>(props.tendering.properties.factSettings?.sections ?? []);
    const [tenderingParts, setTenderingParts] = useState<TenderingPart[]>(props.tendering.tenderingParts);
    const [questions, setQuestions] = useState<Question[]>(
      tenderingParts.flatMap(part => part.questions ?? [])
    );
    const [open, setOpen] = useState(false);
    const [dialogAnswer, setDialogAnswer] = useState<TenderingFactMapping>();
    const [hideNegativeAnswers, setHideNegativeAnswers] = useState<boolean>(true);
    const [openItems, setOpenItems] = useState<string[]>([]);

    const toggleDialog = (mapping: TenderingFactMapping, documentCreated?: boolean) => {
        if(documentCreated) {
            console.log('Document was created for mapping:', mapping);
        }
        setDialogAnswer(mapping);
        setOpen(!open);
    }

    const {user, getFeaturePermissionsByModuleID, featurePermissions: userPermissions } = useAppContext();
    const [featurePermissions, setFeaturePermissions] = useState<any[]>([]); 

    useEffect(() => {
      const permissions = getFeaturePermissionsByModuleID(props.tenderDocument.tenderID);
      setFeaturePermissions(permissions);
    }, [props.tenderDocument.tenderID, user]);

    useEffect(() => {
      const initializeData = async () => {
        const documents = await getDocumentsByModule(props.tenderDocument.tenderID);
        
        const updatedSections = sections.map(section => ({
          ...section,
          mappings: section.mappings.map(element => {
            const answer = answers.find(a => a.questionID === element.questionID);
            const question = questions.find(q => q.questionID === element.questionID);
            
            let elementAnswer = answer;
            if (!elementAnswer) {
                elementAnswer = {
                    questionID: element.questionID,
                    tenderID: props.tenderDocument.tenderID,
                    type: 'string',
                    value: ''
                };
            }
            
            if (!elementAnswer.comment) {
                elementAnswer = {
                    ...elementAnswer,
                    comment: ''
                };
            }

            if((props.overviewQuestionID && element.questionID === props.overviewQuestionID) || section.autoOpenMapping){
              setOpenItems(prev => [...prev, section.name]);
            }
            
            const hasPendingChange = documents.some((doc: any) => doc.new?.questionID === element.questionID);
            
            return {
                ...element,
                answer: {
                  ...elementAnswer,
                  pendingChange: hasPendingChange
                },
                question: question
            };
          })
        }));
        
        setSections(updatedSections);
      };
      
      initializeData();
    }, []);

    const convertAnswer = (answerValue: string, questionType: string): string | number => {
        switch (questionType) {
            case 'string':
                return answerValue ? String(answerValue).replace(/\n/g, '<br>') : '';
            case 'decimalPriceLinear':
                return Number(answerValue);
            case 'decimalPrice':
                return Number(answerValue);
            case 'bool':
                return answerValue == '1' ? 'Ja' : 'Nej';
            default:
                return answerValue;
        }
    }

    const handleToggle: AccordionToggleEventHandler<string> = (event, data) => {
      setOpenItems(data.openItems);
    };


    return (
    <>
      {sections.some((s: FactSection) => s.areNegativeAnswersHidden) && <div className="mb-4 flex items-center">
        <Checkbox
          checked={hideNegativeAnswers}
          onChange={(_, data) => setHideNegativeAnswers(!!data.checked)}
          label={<span className="ml-2">Dölj negativa svar</span>}
        />
      </div>}
      {sections.filter(section => props.mode === 'hidden' ? section.showInHiddenFacts === true : section.showInFacts !== false).map((section, index) => {
        // Filter mappings if hideNegativeAnswers is checked and section.areNegativeAnswersHidden
        const filteredMappings = hideNegativeAnswers && section.areNegativeAnswersHidden
          ? section.mappings.filter(element => {
              return !(
                (element.answer?.type === 'bool' && element.answer?.value == '0') ||
                element.answer?.value === 'Nej' ||
                element.answer?.value === 'Nej och utan kommentar'
              );
            })
          : section.mappings;
        return (
          <Accordion collapsible key={index} openItems={openItems} onToggle={handleToggle}>
            <AccordionItem value={section.name}>
              <AccordionHeader>
                <Text>{section.name}</Text>
              </AccordionHeader>
              <AccordionPanel>
                <Table>
                  {filteredMappings.map((mapping, mappingIndex) => (
                    <TableRow className="group" key={mappingIndex}>
                      <TableCell>
                        <div className="flex justify-between items-center">
                          <div className="flex flex-col gap-1 p-2">
                            <Caption1 italic>{mapping.heading}</Caption1>
                            <Text weight="semibold">{mapping.text}</Text>
                            <div dangerouslySetInnerHTML={{ __html: String(convertAnswer(mapping.answer?.value ?? '', mapping.answer?.type ?? '')) }}></div>
                            <Text italic>{mapping.answer?.comment}</Text>
                          </div>
                          {featurePermissions?.some(fp => fp.action == 'Tender page/Request answer change')
                            && (mapping.permission == 'editable' || mapping.permission == 'approval')
                            && <Button disabled={mapping.answer?.pendingChange} onClick={() => mapping.answer && toggleDialog(mapping)} className="flex-shrink-0 opacity-0 group-hover:opacity-100">Meddela ändring</Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </Table>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        );
      })}
      {dialogAnswer && <AnswerChange
        mapping={dialogAnswer}
        tender={props.tenderDocument}
        open={open}
        onClose={(documentCreated) => {
          toggleDialog(dialogAnswer, documentCreated);
          if (documentCreated) {
            setSections(prevSections => prevSections.map(section => ({
              ...section,
              mappings: section.mappings.map(mapping =>
                mapping.questionID === dialogAnswer.questionID 
                  ? {
                      ...mapping,
                      answer: {
                        ...mapping.answer!,
                        pendingChange: true
                      }
                    }
                  : mapping
              )
            })));
          }
        }}
        updateAnswer={(updatedMapping) => {
          // Update the state with the updated mapping
          setSections(prevSections => prevSections.map(section => ({
            ...section,
            mappings: section.mappings.map(mapping =>
              mapping.questionID === updatedMapping.questionID ? updatedMapping : mapping
            )
          })));
        }}
        updateTender={(updatedTender) => {
          // Update the tender in parent
          if (props.updateTender) {
            props.updateTender(updatedTender);
          }
          // Update the local answers state and mappings
          setAnswers(updatedTender.tenderAnswers ?? []);
          setSections(prevSections => prevSections.map(section => ({
            ...section,
            mappings: section.mappings.map(mapping => {
              const updatedAnswer = updatedTender.tenderAnswers?.find(a => a.questionID === mapping.questionID);
              if (updatedAnswer) {
                return {
                  ...mapping,
                  answer: updatedAnswer
                };
              }
              return mapping;
            })
          })));
        }}
      />}
    </>
    );
}

export default TenderFacts;